import { format, isValid, parseISO } from "date-fns";
import React from "react";

import { toMinorUnits, zeroMoney } from "@performyard/money";
import { Text } from "@performyard/py-components";

import type { DecisionRow, DecisionRowApprovalSnapshot } from "../../../types";
import type { CompensationAuditLogEntry } from "./types";

import { ApprovedBudgetLine, EditedSalaryLine, SalaryUpdateLine, SubmittedBudgetLine } from "./lineItems";

// -- Helpers ------------------------------------------------------------------
export const formatAuditSubmittedDate = (value: string | Date | null | undefined): string => {
	if (value == null || value === "") {
		return "—";
	}
	if (value instanceof Date) {
		return isValid(value) ? format(value, "EEE, MMMM d yyyy") : "—";
	}
	const parsed = parseISO(value);
	const date = isValid(parsed) ? parsed : new Date(value);
	if (!isValid(date)) {
		return "—";
	}
	return format(date, "EEE, MMMM d yyyy");
};

/** ISO 8601 datetime → YYYY-MM-DD (UTC) for day-only comparisons. */
const utcDateKey = (iso: string) => iso.slice(0, 10);

const rowWithSubmitSnapshotForSalaryLine = (user: DecisionRow): DecisionRow => {
	const submitEntry = user.approval_entries?.find((a) => a.status === "submitted");
	if (submitEntry == null) return user;
	return {
		...user,
		proposed_increase_amount: submitEntry.proposed_increase_amount ?? user.proposed_increase_amount,
		proposed_increase_percent: submitEntry.proposed_increase_percent ?? user.proposed_increase_percent,
		proposed_salary: submitEntry.proposed_salary ?? user.proposed_salary,
	};
};

// -- Phase registry -----------------------------------------------------------

type PhaseContext = {
	rows: DecisionRow[];
	decidedAt: string | null;
	firstEntry: DecisionRowApprovalSnapshot;
	getActorNameForRow: (row: DecisionRow) => string | null;
	getActorNameById: (id: string) => string | null;
	/** Fallback timestamp when no `decided_at` exists (e.g. row.updatedAt). */
	fallbackTimestamp: Date | null;
};

type PhaseConfig = {
	renderHeader: (ctx: PhaseContext) => React.ReactNode;
	renderDetailRows?: (ctx: PhaseContext) => React.ReactNode;
};

const PHASE_REGISTRY: Record<string, PhaseConfig> = {
	submitted: {
		renderHeader: (ctx) => {
			return <SubmittedBudgetLine actorName={ctx?.firstEntry?.approver_name || null} />;
		},
		renderDetailRows: (ctx) => {
			return ctx.rows.map((user) => {
				const entry = user?.approval_entries?.find((a) => a.status === ctx.firstEntry.status);
				const actorName = entry?.approver_name || ctx?.firstEntry?.approver_name || null;
				const salaryUser = rowWithSubmitSnapshotForSalaryLine(user);
				return <SalaryUpdateLine key={user._id} user={salaryUser} actorName={actorName} />;
			});
		},
	},
	"cycle-owner-edit": {
		renderHeader: (ctx) => {
			const editLines: {
				row: DecisionRow;
				entry: DecisionRowApprovalSnapshot;
				prev: DecisionRowApprovalSnapshot | null;
			}[] = [];
			for (const row of ctx.rows) {
				const entries = row.approval_entries ?? [];
				const idx = entries.indexOf(ctx.firstEntry);
				if (idx !== -1) {
					editLines.push({ row, entry: entries[idx], prev: idx > 0 ? entries[idx - 1] : null });
				}
			}
			return (
				<>
					{editLines.map(({ row, entry, prev }, idx) => {
						// Compared on exact minor units. As two floats this decided the
						// increased/decreased wording and the green/red color of a
						// compliance-facing log from values that had already been rounded.
						const increased =
							prev != null
								? toMinorUnits(entry.proposed_salary ?? zeroMoney()) >=
									toMinorUnits(prev.proposed_salary ?? zeroMoney())
								: true;
						const actorName = entry.approver_name || ctx.getActorNameById(entry.approver_id ?? "") || null;
						return (
							<EditedSalaryLine
								key={`${row._id}-${idx}`}
								actorName={actorName}
								employeeName={row.employee_name}
								increased={increased}
								amount={entry.proposed_salary ?? null}
								percent={entry.proposed_increase_percent ?? null}
							/>
						);
					})}
				</>
			);
		},
	},
	approved: {
		renderHeader: (ctx) => {
			return <ApprovedBudgetLine actorName={ctx?.firstEntry?.approver_name || null} />;
		},
	},
};

// -- Phase extraction ---------------------------------------------------------

type Phase = {
	phaseKey: string;
	status: string;
	decidedAt: string | null;
	firstEntry: DecisionRowApprovalSnapshot;
	rows: DecisionRow[];
};

const getPhaseKey = (entry: DecisionRowApprovalSnapshot, counter: { value: number }): string => {
	if (entry.notes === "cycle-owner-edit") return `cycle-owner-edit-${++counter.value}`;
	return entry.status;
};

const getDistinctPhases = (rows: DecisionRow[]): Phase[] => {
	const phaseMap = new Map<string, Phase>();

	const counter = { value: 0 };
	for (const row of rows) {
		for (const entry of row.approval_entries ?? []) {
			const key = getPhaseKey(entry, counter);
			const existing = phaseMap.get(key);
			if (existing == null) {
				phaseMap.set(key, {
					phaseKey: key,
					status: entry.status,
					decidedAt: entry.decided_at,
					firstEntry: entry,
					rows: [row],
				});
			} else {
				if (!existing.rows.includes(row)) {
					existing.rows.push(row);
				}
				if (
					entry.decided_at != null &&
					(existing?.decidedAt == null || utcDateKey(entry.decided_at) > utcDateKey(existing.decidedAt))
				) {
					existing.decidedAt = entry.decided_at;
				}
			}
		}
	}

	// Sort oldest-first so the timeline reads bottom-to-top (newest last → rendered first after reverse).
	const sorted = [...phaseMap.values()].sort((a, b) => {
		if (a.decidedAt == null && b.decidedAt == null) return 0;
		if (a.decidedAt == null) return -1;
		if (b.decidedAt == null) return 1;
		return a.decidedAt < b.decidedAt ? -1 : a.decidedAt > b.decidedAt ? 1 : 0;
	});

	// Reverse so newest phase renders on top.
	return sorted.reverse();
};

// -- Main builder -------------------------------------------------------------

export const buildAuditLogEntries = (
	managerId: string,
	baseGroupRowData: DecisionRow[],
	getActorNameForRow: (row: DecisionRow) => string | null,
	getActorNameById: (id: string) => string | null = () => null
): CompensationAuditLogEntry[] => {
	if (baseGroupRowData.length === 0) return [];

	// Build an ID→name map from all approval entries so we can resolve names
	// even when a specific entry (e.g. cycle-owner-edit) has no approver_name.
	const approverNameMap = new Map<string, string>();
	for (const row of baseGroupRowData) {
		for (const entry of row.approval_entries ?? []) {
			if (entry.approver_id && entry.approver_name?.trim()) {
				approverNameMap.set(entry.approver_id, entry.approver_name.trim());
			}
		}
	}
	const resolveActorName = (id: string): string | null => approverNameMap.get(id) ?? getActorNameById(id);

	const fallbackTimestamp = baseGroupRowData[0]?.updatedAt ?? null;

	// Timeline-based rendering: use approval_entries when available.
	const phases = getDistinctPhases(baseGroupRowData);

	if (phases.length > 0) {
		// Group all phases by day so entries on the same day share a single date header.
		const dayMap = new Map<string, { dayKey: string; dateLabel: string; phases: Phase[] }>();
		for (const phase of phases) {
			const registryKey = phase.phaseKey.startsWith("cycle-owner-edit") ? "cycle-owner-edit" : phase.phaseKey;
			if (PHASE_REGISTRY[registryKey] == null) continue;

			const fallbackStr =
				fallbackTimestamp instanceof Date
					? fallbackTimestamp.toISOString()
					: (fallbackTimestamp as string | null);
			const rawDate = phase.decidedAt ?? fallbackStr;
			const dayKey = rawDate ? utcDateKey(rawDate) : "unknown";

			const existing = dayMap.get(dayKey);
			if (existing != null) {
				existing.phases.push(phase);
			} else {
				dayMap.set(dayKey, {
					dayKey,
					dateLabel: formatAuditSubmittedDate(phase.decidedAt ?? fallbackTimestamp),
					phases: [phase],
				});
			}
		}
		// Sort day groups newest-first.
		const dayGroups = [...dayMap.values()].sort((a, b) => (a.dayKey > b.dayKey ? -1 : a.dayKey < b.dayKey ? 1 : 0));

		const sections = dayGroups
			.map((group) => {
				const phaseElements = group.phases
					.map((phase) => {
						const registryKey = phase.phaseKey.startsWith("cycle-owner-edit")
							? "cycle-owner-edit"
							: phase.phaseKey;
						const config = PHASE_REGISTRY[registryKey];
						if (config == null) return null;

						const ctx: PhaseContext = {
							rows: phase.rows,
							decidedAt: phase.decidedAt,
							firstEntry: phase.firstEntry,
							getActorNameForRow,
							getActorNameById: resolveActorName,
							fallbackTimestamp,
						};

						return (
							<React.Fragment key={phase.phaseKey}>
								{config.renderHeader(ctx)}
								{config.renderDetailRows?.(ctx)}
							</React.Fragment>
						);
					})
					.filter(Boolean);

				if (phaseElements.length === 0) return null;

				return (
					<React.Fragment key={group.dayKey}>
						<Text weight="regular" size="caption">
							{group.dateLabel}
						</Text>
						{phaseElements}
					</React.Fragment>
				);
			})
			.filter(Boolean);

		if (sections.length > 0) {
			return [
				{
					id: `${managerId}-audit-timeline`,
					content: <div className="flex w-full flex-col gap-2">{sections}</div>,
				},
			];
		}
	}

	// Fallback: rows are in_review but have no approval_entries yet (pre-submit state).
	const inReviewRows = baseGroupRowData.filter((row) => row.status === "in_review");
	if (inReviewRows.length === 0) return [];

	const allInReview = baseGroupRowData.every((row) => row.status === "in_review");
	const headerRow = inReviewRows[0];

	return [
		{
			id: `${managerId}-${allInReview ? "submitted-activity-group" : "salary-updates"}`,
			content: (
				<div className="flex w-full flex-col gap-2">
					{allInReview && (
						<>
							<Text weight="regular" size="caption">
								{formatAuditSubmittedDate(fallbackTimestamp)}
							</Text>
							<SubmittedBudgetLine actorName={getActorNameForRow(headerRow)} />
						</>
					)}
					{inReviewRows.map((user) => (
						<SalaryUpdateLine key={user._id} user={user} actorName={getActorNameForRow(user)} />
					))}
				</div>
			),
		},
	];
};
